import type { ExportFormatStructureNode, ColumnFormatSpec, XmlAttribute } from "@capture/shared";
import { FormatSpecEditor } from "../../form-config/export/FormatSpecEditor.js";
import { AttributeRow } from "../../form-config/export/AttributeRow.js";
import styles from "../../form-config/export/export-composer.module.css";

const EMPTY_SPEC: Record<ColumnFormatSpec["kind"], ColumnFormatSpec> = {
  raw: { kind: "raw" },
  text: { kind: "text" },
  date: { kind: "date", token: "YYYY-MM-DD" },
  number: { kind: "number" },
};

export function SlotPropertiesPanel({
  node,
  onChange,
}: {
  node: ExportFormatStructureNode | null;
  onChange: (next: ExportFormatStructureNode) => void;
}) {
  if (!node) {
    return <div className={styles.structureRightPane}><p className="rb-muted">Select a slot to edit its properties.</p></div>;
  }

  const isField = node.type === "field";
  const dataType = node.dataType ?? "raw";

  return (
    <div className={styles.structureRightPane}>
      <div className="rb-row">
        <label className="rb-label" htmlFor="slot-name">Name</label>
        <input id="slot-name" className="rb-input" value={node.name}
          onChange={(e) => onChange({ ...node, name: e.target.value })} />
      </div>

      {isField && (
        <>
          <div className="rb-row">
            <label className="rb-label" htmlFor="slot-type">Type</label>
            <select id="slot-type" aria-label="Type" className="rb-input" value={dataType}
              onChange={(e) => {
                const kind = e.target.value as ColumnFormatSpec["kind"];
                onChange({ ...node, dataType: kind, format: EMPTY_SPEC[kind] });
              }}>
              <option value="raw">Raw</option>
              <option value="text">Text</option>
              <option value="date">Date</option>
              <option value="number">Number</option>
            </select>
          </div>

          <FormatSpecEditor
            spec={node.format ?? EMPTY_SPEC[dataType]}
            onChange={(next) => onChange({ ...node, format: next })}
            idPrefix="slot"
          />

          <label className="rb-fmt-note" style={{ display: "block" }}>
            <input type="checkbox" aria-label="Required" checked={node.required ?? false}
              onChange={(e) => onChange({ ...node, required: e.target.checked })} /> Required
          </label>
          <label className="rb-fmt-note" style={{ display: "block" }}>
            <input type="checkbox" aria-label="Allow empty" checked={node.allowEmpty ?? true}
              onChange={(e) => onChange({ ...node, allowEmpty: e.target.checked })} /> Allow empty
          </label>
        </>
      )}

      {!isField && (
        <p className="rb-muted">Container ({node.type}) — rename above, or add child slots in the tree.</p>
      )}

      {/* XML attributes — available for all node types */}
      <div style={{ marginTop: 12 }}>
        <div className="rb-label" style={{ marginBottom: 4 }}>XML attributes</div>
        {(node.attributes ?? []).map((attr, idx) => (
          <AttributeRow
            key={idx}
            attr={attr}
            availableFields={[]}
            onUpdate={(next: XmlAttribute) => {
              const nextAttrs = (node.attributes ?? []).map((a, i) => (i === idx ? next : a));
              onChange({ ...node, attributes: nextAttrs });
            }}
            onDelete={() => {
              const nextAttrs = (node.attributes ?? []).filter((_, i) => i !== idx);
              if (nextAttrs.length === 0) {
                const { attributes: _removed, ...rest } = node;
                onChange(rest as ExportFormatStructureNode);
              } else {
                onChange({ ...node, attributes: nextAttrs });
              }
            }}
          />
        ))}
        <button
          type="button"
          onClick={() => {
            const blank: XmlAttribute = { name: "", source: "literal", literal: "" };
            onChange({ ...node, attributes: [...(node.attributes ?? []), blank] });
          }}
        >
          Add attribute
        </button>
      </div>
    </div>
  );
}
